/**
 * Eager registry for BDUI module entity domains and shared model blocks.
 * Loaded once at startup via import.meta.glob — used by createDefaultContext().
 *
 * Explicit JSON imports below guarantee new shared/module files are bundled even when
 * Vite's glob cache has not picked up a freshly added path (dev-server restart otherwise required).
 */

import awardDomain from '../../../domain/src/modules/rsu-award/rsu-award.domain.json';
import psuAwardDomain from '../../../domain/src/modules/psu-award/psu-award.domain.json';
import optionAwardDomain from '../../../domain/src/modules/option-award/option-award.domain.json';
import participantDomain from '../../../domain/src/modules/participant/participant.domain.json';
import settlementDomain from '../../../domain/src/modules/settlement/settlement.domain.json';
import transferAndAllocateDomain from '../../../domain/src/modules/transfer-and-allocate/transfer-and-allocate.domain.json';
import releaseDomain from '../../../domain/src/modules/release/release.domain.json';
import purchasePeriodDomain from '../../../domain/src/modules/purchase-period/purchase-period.domain.json';
import departmentDomain from '../../../domain/src/modules/department/department.domain.json';
import jobTitleDomain from '../../../domain/src/modules/job-title/job-title.domain.json';
import employeeTypeDomain from '../../../domain/src/modules/employee-type/employee-type.domain.json';
import taxPeriodDomain from '../../../domain/src/modules/tax-period/tax-period.domain.json';
import esppDomain from '../../../domain/src/modules/espp/espp.domain.json';
import planManagementDomain from '../../../domain/src/modules/plan-management/plan-management.domain.json';
import grantManagementDomain from '../../../domain/src/modules/grant-management/grant-management.domain.json';
import vestingTemplateDomain from '../../../domain/src/modules/vesting-template/vesting-template.domain.json';
import terminationRulesDomain from '../../../domain/src/modules/termination-rules/termination-rules.domain.json';
import performanceRuleDomain from '../../../domain/src/modules/performance-rule/performance-rule.domain.json';
import sharePoolDomain from '../../../domain/src/modules/share-pool/share-pool.domain.json';
import grantDomain from '../../../domain/src/modules/grant/grant.domain.json';
import termsConditionsDomain from '../../../domain/src/modules/terms-conditions/terms-conditions.domain.json';
import feeDomain from '../../../domain/src/modules/fee/fee.domain.json';
import sandboxDomain from '../../../domain/src/modules/sandbox/sandbox.domain.json';
import participantBroadcastDomain from '../../../domain/src/modules/participant-broadcast/participant-broadcast.domain.json';
import userDefinedFieldDomain from '../../../domain/src/modules/user-defined-field/user-defined-field.domain.json';
import retirementEligibilityConfigDomain from '../../../domain/src/modules/retirement-eligibility-config/retirement-eligibility-config.domain.json';
import alertSubscriptionConfigDomain from '../../../domain/src/modules/alert-subscription-config/alert-subscription-config.domain.json';
import emailTemplateDomain from '../../../domain/src/modules/email-template/email-template.domain.json';
import transactionConfigDomain from '../../../domain/src/modules/transaction-config/transaction-config.domain.json';
import bankAccountDomain from '../../../domain/src/modules/bank-account/bank-account.domain.json';
import bankAccountPurposeDomain from '../../../domain/src/modules/bank-account-purpose/bank-account-purpose.domain.json';
import customerSuccessAutomationDomain from '../../../domain/src/modules/customer-success-automation/customer-success-automation.domain.json';
import companyMappingDomain from '../../../domain/src/modules/company-mapping/company-mapping.domain.json';
import rolePublishDomain from '../../../domain/src/modules/role-publish/role-publish.domain.json';
import roleMappingDomain from '../../../domain/src/modules/role-mapping/role-mapping.domain.json';
import jobSchedulerDomain from '../../../domain/src/modules/job-scheduler/job-scheduler.domain.json';
import settlementRules from '../../../domain/src/shared/settlement/settlement.rules.json';
import awardAdjustmentRules from '../../../domain/src/shared/award-adjustment/award-adjustment.rules.json';

export interface ModuleRegistryMaps {
  moduleDomain: Map<string, Record<string, unknown>>;
  sharedModel: Map<string, Record<string, unknown>>;
  sharedOrch: Map<string, Record<string, unknown>>;
  sharedRules: Map<string, Record<string, unknown>>;
}

const RAW_MODULE_DOMAIN = import.meta.glob(
  '../../../domain/src/modules/*/*.domain.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

const RAW_SHARED_MODEL = import.meta.glob(
  '../../../domain/src/shared/*/*.model.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

const RAW_SHARED_ORCH = import.meta.glob(
  '../../../domain/src/shared/*/*.orch.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

const RAW_SHARED_RULES = import.meta.glob(
  '../../../domain/src/shared/*/*.rules.json',
  { eager: true },
) as Record<string, { default: Record<string, unknown> }>;

function entityFromModuleDomainPath(path: string): string | null {
  const match = path.match(/\/modules\/([^/]+)\/[^/]+\.domain\.json$/);
  return match?.[1] ?? null;
}

function nameFromSharedPath(path: string, tier: string): string | null {
  const match = path.match(new RegExp(`shared/([^/]+)/[^/]+\\.${tier}\\.json`));
  return match?.[1] ?? null;
}

const EXPLICIT_MODULE_DOMAINS: Record<string, Record<string, unknown>> = {
  'rsu-award': awardDomain as Record<string, unknown>,
  'psu-award': psuAwardDomain as Record<string, unknown>,
  'option-award': optionAwardDomain as Record<string, unknown>,
  participant: participantDomain as Record<string, unknown>,
  settlement: settlementDomain as Record<string, unknown>,
  'transfer-and-allocate': transferAndAllocateDomain as Record<string, unknown>,
  release: releaseDomain as Record<string, unknown>,
  'purchase-period': purchasePeriodDomain as Record<string, unknown>,
  department: departmentDomain as Record<string, unknown>,
  'job-title': jobTitleDomain as Record<string, unknown>,
  'employee-type': employeeTypeDomain as Record<string, unknown>,
  'tax-period': taxPeriodDomain as Record<string, unknown>,
  espp: esppDomain as Record<string, unknown>,
  'plan-management': planManagementDomain as Record<string, unknown>,
  'grant-management': grantManagementDomain as Record<string, unknown>,
  'vesting-template': vestingTemplateDomain as Record<string, unknown>,
  'termination-rules': terminationRulesDomain as Record<string, unknown>,
  'performance-rule': performanceRuleDomain as Record<string, unknown>,
  'share-pool': sharePoolDomain as Record<string, unknown>,
  grant: grantDomain as Record<string, unknown>,
  'terms-conditions': termsConditionsDomain as Record<string, unknown>,
  fee: feeDomain as Record<string, unknown>,
  sandbox: sandboxDomain as Record<string, unknown>,
  'participant-broadcast': participantBroadcastDomain as Record<string, unknown>,
  'user-defined-field': userDefinedFieldDomain as Record<string, unknown>,
  'retirement-eligibility-config': retirementEligibilityConfigDomain as Record<string, unknown>,
  'alert-subscription-config': alertSubscriptionConfigDomain as Record<string, unknown>,
  'email-template': emailTemplateDomain as Record<string, unknown>,
  'transaction-config': transactionConfigDomain as Record<string, unknown>,
  'bank-account': bankAccountDomain as Record<string, unknown>,
  'bank-account-purpose': bankAccountPurposeDomain as Record<string, unknown>,
  'customer-success-automation': customerSuccessAutomationDomain as Record<string, unknown>,
  'company-mapping': companyMappingDomain as Record<string, unknown>,
  'role-publish': rolePublishDomain as Record<string, unknown>,
  'role-mapping': roleMappingDomain as Record<string, unknown>,
  'job-scheduler': jobSchedulerDomain as Record<string, unknown>,
};

const EXPLICIT_SHARED_MODEL: Record<string, Record<string, unknown>> = {};

const EXPLICIT_SHARED_RULES: Record<string, Record<string, unknown>> = {
  settlement: settlementRules as Record<string, unknown>,
  'award-adjustment': awardAdjustmentRules as Record<string, unknown>,
};

export function buildModuleRegistryMaps(): ModuleRegistryMaps {
  const moduleDomain = new Map<string, Record<string, unknown>>();
  const sharedModel = new Map<string, Record<string, unknown>>();
  const sharedOrch = new Map<string, Record<string, unknown>>();
  const sharedRules = new Map<string, Record<string, unknown>>();

  for (const [path, mod] of Object.entries(RAW_MODULE_DOMAIN)) {
    const entity = entityFromModuleDomainPath(path);
    if (entity && mod.default) moduleDomain.set(entity, mod.default);
  }

  for (const [path, mod] of Object.entries(RAW_SHARED_MODEL)) {
    const name = nameFromSharedPath(path, 'model');
    if (name && mod.default) sharedModel.set(name, mod.default);
  }

  for (const [path, mod] of Object.entries(RAW_SHARED_ORCH)) {
    const name = nameFromSharedPath(path, 'orch');
    if (name && mod.default) sharedOrch.set(name, mod.default);
  }

  for (const [path, mod] of Object.entries(RAW_SHARED_RULES)) {
    const name = nameFromSharedPath(path, 'rules');
    if (name && mod.default) sharedRules.set(name, mod.default);
  }

  // Static imports override glob so bundled entity domains are always the explicit JSON modules.
  for (const [name, doc] of Object.entries(EXPLICIT_MODULE_DOMAINS)) {
    moduleDomain.set(name, doc);
  }

  for (const [name, doc] of Object.entries(EXPLICIT_SHARED_MODEL)) {
    if (!sharedModel.has(name)) sharedModel.set(name, doc);
  }

  for (const [name, doc] of Object.entries(EXPLICIT_SHARED_RULES)) {
    if (!sharedRules.has(name)) sharedRules.set(name, doc);
  }

  return { moduleDomain, sharedModel, sharedOrch, sharedRules };
}
